import type {
  ObjectInventoryField,
  ObjectInventoryFieldItem,
} from '@/entities/Inventory/ObjectTypes'
import { fieldTypeNameFromId, fieldTypeNameToLegacyType } from './fieldTypeLegacy'
import type {
  ObjectPassportFieldDefinition,
  ObjectPassportFieldProps,
  ObjectPassportFieldValue,
  ObjectPassportFieldsPayload,
} from './types'

/**
 * Нормализованный паспорт готов к использованию, когда в ответе есть описания полей.
 */
export function shouldUseNormalizedObjectPassport(
  payload: ObjectPassportFieldsPayload | null | undefined,
): boolean {
  return Boolean(payload && payload.fields.length > 0)
}

/**
 * Поле размещено на гриде паспорта (не только в палитре).
 */
export function isObjectPassportFieldOnGrid(field: ObjectPassportFieldDefinition): boolean {
  return field.field_props.some((props) => props.table_view_order != null)
}

/**
 * Ячейка грида — табличный перечень (LIST), не скалярный input.
 */
export function isObjectPassportTableField(field: ObjectPassportFieldDefinition): boolean {
  return field.field_props.some((props) => props.table_view_order?.view_type === 'table')
}

/**
 * Поле справочника (catalog) в нормализованной модели.
 */
export function isObjectPassportCatalogField(field: ObjectPassportFieldDefinition): boolean {
  return fieldTypeNameFromId(field.field_type) === 'catalog'
}

const REFERENCE_FIELD_TYPE_NAMES = new Set([
  'one_to_one',
  'one_to_many',
  'many_to_one',
  'many_to_many',
])

/**
 * Поле связи с объектом инвентаря (`reference_id` → catalog type id).
 */
export function isObjectPassportReferenceField(field: ObjectPassportFieldDefinition): boolean {
  const typeName = fieldTypeNameFromId(field.field_type)
  if (!REFERENCE_FIELD_TYPE_NAMES.has(typeName)) return false
  const referenceId = Number(field.reference_id)
  return Number.isFinite(referenceId) && referenceId > 0
}

/**
 * Поле, значение которого выбирается через модалку (справочник или связь с объектом).
 */
export function isObjectPassportSelectableField(field: ObjectPassportFieldDefinition): boolean {
  return isObjectPassportCatalogField(field) || isObjectPassportReferenceField(field)
}

/**
 * Размещение поля на гриде — табличный вид (`view_type: table`).
 */
export function isObjectPassportPlacementTableView(
  placement: ObjectPassportFieldProps | null | undefined,
): boolean {
  return placement?.table_view_order?.view_type === 'table'
}

/**
 * Размещение с display-SQL (`pseudo_sql`) — только чтение в runtime.
 */
export function isObjectPassportPlacementDisplayField(
  placement: ObjectPassportFieldProps | null | undefined,
): boolean {
  return Boolean(placement?.pseudo_sql?.trim())
}

/**
 * Показывать pseudo_sql-текст размещения вместо значения связи/справочника.
 * Для reference-полей с модалкой выбора stub pseudo_sql не используется.
 */
export function isObjectPassportPlacementPseudoDisplay(
  placement: ObjectPassportFieldProps | null | undefined,
  field: ObjectPassportFieldDefinition,
  interactiveSelect = false,
): boolean {
  if (!isObjectPassportPlacementDisplayField(placement)) return false
  if (interactiveSelect && isObjectPassportReferenceField(field)) {
    if (placement?.value_result?.trim()) return true
    return false
  }
  return true
}

/**
 * Возвращает размещение поля по `field_layout_id` ячейки грида.
 */
export function objectPassportFieldPlacement(
  field: ObjectPassportFieldDefinition,
  placementLayoutId: number | null | undefined,
): ObjectPassportFieldProps | null {
  const id = Number(placementLayoutId)
  if (!Number.isFinite(id) || id <= 0) return null
  return field.field_props.find((props) => props.id === id) ?? null
}

/**
 * Текст display-размещения: `value_result` конкретного `field_props`, иначе скаляр значения.
 */
export function objectPassportPlacementDisplayText(
  field: ObjectPassportFieldDefinition,
  placementLayoutId: number | null | undefined,
  value: ObjectPassportFieldValue | null | undefined,
): string {
  const placement = objectPassportFieldPlacement(field, placementLayoutId)
  const placementDisplay = placement?.value_result?.trim()
  if (placement?.pseudo_sql?.trim() && placementDisplay) return placementDisplay
  if (value) return objectPassportValueToRuntimeScalar(value)
  return ''
}

/**
 * Display-поле с pseudo_sql в табличном размещении (view_type === 'table').
 */
export function isObjectPassportDisplayField(field: ObjectPassportFieldDefinition): boolean {
  return field.field_props
    .filter((props) => props.table_view_order?.view_type === 'table')
    .some((props) => Boolean(props.pseudo_sql?.trim()))
}

/**
 * Скаляр для отображения: для display-полей предпочитаем value_result.
 */
export function objectPassportValueToRuntimeScalar(value: ObjectPassportFieldValue): string {
  const display = value.value_result?.trim()
  if (display) return display

  if (value.value != null && value.value !== '') return value.value
  if (value.value_list?.length) return value.value_list.map(String).join(',')
  return ''
}

/**
 * Сырое значение для редактирования (без pseudo_sql display).
 */
export function objectPassportValueToEditScalar(value: ObjectPassportFieldValue): string {
  if (value.value != null && value.value !== '') return value.value
  if (value.value_list?.length) return value.value_list.map(String).join(',')
  return ''
}

/**
 * Ищет значение поля в payloads.values / data[].
 */
export function objectPassportValueForField(
  fieldId: number,
  values: ObjectPassportFieldValue[],
): ObjectPassportFieldValue | null {
  return values.find((item) => item.field_id === fieldId) ?? null
}

/**
 * Преобразует values[] в записи `{ id, value }` для карты значений.
 */
export function objectPassportValuesToRuntimeEntries(
  values: ObjectPassportFieldValue[],
  mode: 'display' | 'edit' = 'display',
): Array<{ id: string; value: string }> {
  const toScalar =
    mode === 'edit' ? objectPassportValueToEditScalar : objectPassportValueToRuntimeScalar

  return values.map((item) => ({
    id: String(item.field_id),
    value: toScalar(item),
  }))
}

/**
 * Строит карту значений полей из нормализованного ответа.
 */
export function buildObjectPassportFieldValueMap(
  fields: ObjectPassportFieldDefinition[],
  values: ObjectPassportFieldValue[],
  mode: 'display' | 'edit' = 'edit',
): Record<string, string> {
  const metadata = objectPassportFieldsToInventoryFields(fields)
  const currentValues = new Map(
    objectPassportValuesToRuntimeEntries(values, mode).map((item) => [item.id, item.value]),
  )

  return Object.fromEntries(
    metadata.map((field) => {
      const fieldId = String(field.id)
      const fallback =
        field.default_value == null || field.default_value === '' ? '' : String(field.default_value)
      return [fieldId, currentValues.get(fieldId) ?? fallback]
    }),
  )
}

/**
 * Поле с типом boolean в нормализованной модели.
 */
export function isObjectPassportBooleanField(field: ObjectPassportFieldDefinition): boolean {
  return fieldTypeNameFromId(field.field_type) === 'boolean'
}

const parseValidationItems = (items: unknown): ObjectInventoryFieldItem[] => {
  if (!Array.isArray(items)) return []
  return items
    .map((item) => {
      if (item == null || typeof item !== 'object') return null
      const row = item as Record<string, unknown>
      const id = Number(row.id)
      if (!Number.isFinite(id)) return null
      return { id, name: String(row.name ?? '') }
    })
    .filter((item): item is ObjectInventoryFieldItem => item != null)
}

const fieldInventoryType = (field: ObjectPassportFieldDefinition, typeName: string): number => {
  if (typeName === 'catalog') return field.catalog_id ?? 0
  if (field.reference_id != null) return field.reference_id
  return field.catalog_objects_type_id
}

/**
 * Разрешает целевой inventory-type-ID поля связи (для запроса элементов).
 */
export function resolveFieldInventoryTypeId(field: ObjectPassportFieldDefinition): number {
  return fieldInventoryType(field, fieldTypeNameFromId(field.field_type))
}

/**
 * Возвращает первую раскладку поля с координатами грида.
 */
const firstObjectPassportGridProps = (field: ObjectPassportFieldDefinition) =>
  field.field_props.find((props) => props.table_view_order != null)

/**
 * Адаптирует нормализованное поле к метаданным существующих виджетов ввода.
 */
export function objectPassportFieldDefinitionToInventoryField(
  field: ObjectPassportFieldDefinition,
): ObjectInventoryField {
  const typeName = fieldTypeNameFromId(field.field_type)
  const validationProps = field.validation_props ?? {}
  const isTable = validationProps.is_table === true || isObjectPassportTableField(field)

  return {
    id: String(field.id),
    name: field.name,
    type: fieldTypeNameToLegacyType(typeName),
    items: parseValidationItems(validationProps.items),
    object_type: fieldInventoryType(field, typeName),
    default_value: field.default_value ?? '',
    multiple: Boolean(validationProps.multiple),
    count_object: Boolean(validationProps.count_object),
    is_filtered: Boolean(field.use_in_filter),
    is_table: isTable,
    is_system: Boolean(field.is_system),
    is_global_search: Boolean(field.use_in_search),
    mandatory: Boolean(field.is_required),
    min: validationProps.min as number | string | undefined,
    max: validationProps.max as number | string | undefined,
    length: validationProps.length as number | undefined,
    precision: validationProps.precision as number | undefined,
    use_min: validationProps.use_min as boolean | undefined,
    use_max: validationProps.use_max as boolean | undefined,
    group: firstObjectPassportGridProps(field)?.table_view_order?.group_id ?? undefined,
    ...(field.reference_id != null
      ? {
          link_target_type_id: field.reference_id,
          relation_field_id: field.id,
          cardinality: typeName,
        }
      : {}),
  }
}

/**
 * Собирает метаданные полей runtime-паспорта для существующих виджетов ввода.
 */
export function objectPassportFieldsToInventoryFields(
  fields: ObjectPassportFieldDefinition[],
): ObjectInventoryField[] {
  return fields.map(objectPassportFieldDefinitionToInventoryField)
}
